import type { FeedItem } from "@scrile/contracts";
import { intlLocale, t } from "../i18n";
import type { Outgoing } from "./sendQueue";

/**
 * Лента → строки переписки: разделители дней, «Episode N started», пузыри с группировкой
 * (подряд от одного и того же в пределах GROUP_GAP_MS — одна группа: общий хвост пузыря,
 * портрет героя у последнего), служебные заметки и пейвол.
 *
 * Дни и время — в часовом поясе подписчика (Me.timezone): сюжет идёт по его часам.
 */

export const GROUP_GAP_MS = 5 * 60_000;

export type BubbleSide = "hero" | "me";

/** Что рисует пузырь: элемент ленты или своё неотправленное. */
export type ChatMessage =
  | { source: "feed"; item: FeedItem }
  | { source: "local"; outgoing: Outgoing };

export type TimelineEntry =
  | { type: "day"; key: string; label: string }
  | { type: "episode"; key: string; episode: number; title: string | null }
  | {
      type: "message";
      key: string;
      side: BubbleSide;
      message: ChatMessage;
      at: number;
      time: string;
      /** Первый и последний в группе — у них скругления и портрет. */
      first: boolean;
      last: boolean;
    }
  | { type: "notice"; key: string; tone: "busy" | "info"; text: string; time: string }
  | { type: "paywall"; key: string; episode: number | null };

export interface TimelineInput {
  items: readonly FeedItem[];
  outgoing: readonly Outgoing[];
  timeZone: string | undefined;
  now: number;
  heroName: string;
  /** Названия серий (GET /episodes), если есть: «Episode 2 · Night Shift». */
  episodeTitles?: ReadonlyMap<number, string>;
}

// --- время ----------------------------------------------------------------------

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(key: string, options: Intl.DateTimeFormatOptions, timeZone: string | undefined): Intl.DateTimeFormat {
  const cacheKey = `${key}|${timeZone ?? ""}|${intlLocale()}`;
  let format = formatters.get(cacheKey);
  if (!format) {
    try {
      format = new Intl.DateTimeFormat(key === "day" ? "en-CA" : intlLocale(), timeZone ? { ...options, timeZone } : options);
    } catch {
      // Кривой пояс в профиле — время устройства, а не пустой экран
      format = new Intl.DateTimeFormat(key === "day" ? "en-CA" : intlLocale(), options);
    }
    formatters.set(cacheKey, format);
  }
  return format;
}

/** «2026-09-28» в поясе подписчика — ключ дня для группировки. */
export function dayKey(ms: number, timeZone?: string): string {
  return formatter("day", { year: "numeric", month: "2-digit", day: "2-digit" }, timeZone).format(new Date(ms));
}

/** Разница в календарных днях между ключами дней (b − a). */
function daysBetween(a: string, b: string): number {
  const parse = (key: string) => Date.UTC(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, Number(key.slice(8, 10)));
  return Math.round((parse(b) - parse(a)) / 86_400_000);
}

/** «Today», «Yesterday», «Monday, September 21», в другом году — с годом. */
export function dayLabel(ms: number, now: number, timeZone?: string): string {
  const day = dayKey(ms, timeZone);
  const today = dayKey(now, timeZone);
  const diff = daysBetween(day, today);
  if (diff === 0) return t("messenger.today");
  if (diff === 1) return t("messenger.yesterday");
  const sameYear = day.slice(0, 4) === today.slice(0, 4);
  return formatter(
    sameYear ? "label" : "labelYear",
    sameYear ? { weekday: "long", month: "long", day: "numeric" } : { month: "long", day: "numeric", year: "numeric" },
    timeZone,
  ).format(new Date(ms));
}

/** «9:41 PM» в поясе подписчика. */
export function timeLabel(ms: number, timeZone?: string): string {
  return formatter("time", { hour: "numeric", minute: "2-digit" }, timeZone).format(new Date(ms));
}

// --- строки ---------------------------------------------------------------------

function sideOf(message: ChatMessage): BubbleSide {
  return message.source === "local" || message.item.from === "user" ? "me" : "hero";
}

/** Номер серии реплики сцены (у ответов модели и зеркал его нет). */
function sceneEpisode(item: FeedItem): number | null {
  if (item.from !== "hero") return null;
  switch (item.kind) {
    case "text":
    case "voice":
    case "photo":
    case "video":
    case "hero_card":
      return item.body.episode ?? null;
    default:
      return null;
  }
}

type Row =
  | { kind: "message"; message: ChatMessage; at: number; key: string }
  | { kind: "episode"; episode: number; title: string | null; at: number; key: string }
  | { kind: "notice"; tone: "busy" | "info"; text: string; at: number; key: string }
  | { kind: "paywall"; episode: number | null; at: number; key: string };

function feedRows(items: readonly FeedItem[], input: TimelineInput): Row[] {
  const rows: Row[] = [];
  const shown = new Set<number>();
  const titleOf = (episode: number, fallback: string | null) => fallback ?? input.episodeTitles?.get(episode) ?? null;
  for (const item of items) {
    const at = Date.parse(item.at);
    const key = `s${item.seq}`;
    switch (item.kind) {
      case "episode_ready":
        if (!shown.has(item.body.episode)) {
          shown.add(item.body.episode);
          rows.push({ kind: "episode", episode: item.body.episode, title: titleOf(item.body.episode, item.body.title), at, key });
        }
        continue;
      case "extra_ready":
        rows.push({ kind: "notice", tone: "info", text: t("messenger.extraReady", { title: item.body.title }), at, key });
        continue;
      case "system": {
        const type = item.body.type;
        const text = item.body.text?.trim() ?? "";
        if (type === "reminder") rows.push({ kind: "message", message: { source: "feed", item }, at, key });
        else if (type === "busy") rows.push({ kind: "notice", tone: "busy", text: text || t("messenger.busy", { hero: input.heroName }), at, key });
        else if (type === "paywall") rows.push({ kind: "paywall", episode: item.body.episode ?? null, at, key });
        // Незнакомый тип: с текстом — заметкой, без текста показать нечего
        else if (text) rows.push({ kind: "notice", tone: "info", text, at, key });
        continue;
      }
      default: {
        // Серия началась — разделитель перед её первой репликой, даже если сервер не
        // прислал episode_ready (переписка его не пишет: серия — это её сцены)
        const episode = sceneEpisode(item);
        if (episode !== null && !shown.has(episode)) {
          shown.add(episode);
          rows.push({ kind: "episode", episode, title: titleOf(episode, null), at, key: `e${episode}` });
        }
        rows.push({ kind: "message", message: { source: "feed", item }, at, key });
      }
    }
  }
  return rows;
}

/**
 * Пейвол — только последний и только если после него сюжет не пошёл дальше (подписались —
 * сцена пришла, старую карточку «подпишись» показывать уже незачем).
 */
function prunePaywalls(rows: Row[]): Row[] {
  let lastPaywall = -1;
  rows.forEach((row, index) => {
    if (row.kind === "paywall") lastPaywall = index;
  });
  return rows.filter((row, index) => {
    if (row.kind !== "paywall") return true;
    if (index !== lastPaywall) return false;
    return !rows.slice(index + 1).some((later) => later.kind === "episode" || (later.kind === "message" && sideOf(later.message) === "hero"));
  });
}

export function buildTimeline(input: TimelineInput): TimelineEntry[] {
  const rows = prunePaywalls(feedRows(input.items, input));
  // Своё неотправленное — в конце: у него ещё нет места в ленте. Отправленное с seq —
  // тоже в конце: зеркало придёт следующим ответом и встанет на своё место
  for (const outgoing of input.outgoing) {
    rows.push({ kind: "message", message: { source: "local", outgoing }, at: outgoing.createdAt, key: `l${outgoing.id}` });
  }

  const entries: TimelineEntry[] = [];
  let currentDay: string | null = null;
  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index]!;
    const day = dayKey(row.at, input.timeZone);
    if (day !== currentDay) {
      currentDay = day;
      entries.push({ type: "day", key: `d${day}`, label: dayLabel(row.at, input.now, input.timeZone) });
    }
    switch (row.kind) {
      case "episode":
        entries.push({ type: "episode", key: row.key, episode: row.episode, title: row.title });
        break;
      case "notice":
        entries.push({ type: "notice", key: row.key, tone: row.tone, text: row.text, time: timeLabel(row.at, input.timeZone) });
        break;
      case "paywall":
        entries.push({ type: "paywall", key: row.key, episode: row.episode });
        break;
      case "message": {
        const side = sideOf(row.message);
        const prev = rows[index - 1];
        const next = rows[index + 1];
        const joins = (other: Row | undefined, a: number, b: number) =>
          other?.kind === "message" &&
          sideOf(other.message) === side &&
          Math.abs(b - a) <= GROUP_GAP_MS &&
          dayKey(other.at, input.timeZone) === day;
        entries.push({
          type: "message",
          key: row.key,
          side,
          message: row.message,
          at: row.at,
          time: timeLabel(row.at, input.timeZone),
          first: !joins(prev, prev?.at ?? 0, row.at),
          last: !joins(next, row.at, next?.at ?? 0),
        });
        break;
      }
    }
  }
  return entries;
}

/** Пейвол, который надо показать сейчас (последний в ленте), — чтобы заменить им поле ввода. */
export function activePaywall(entries: readonly TimelineEntry[]): { episode: number | null } | null {
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const entry = entries[i]!;
    if (entry.type === "paywall") return { episode: entry.episode };
  }
  return null;
}
